import { useEffect, useRef, useSyncExternalStore } from 'react'

// Whether the browser has a connection (h 4063-4076, 4167-4171). While it has
// not, the shell shows the offline banner, and every write is refused before
// it is sent (apiJson), so a write is never attempted and half-failed: the
// panel keeps what was typed and says it needs a connection. Reading still
// works from what is on screen.
//
// The browser's own online/offline events are the signal. A request that
// fails for another reason (the API is down) is an ordinary failed request,
// shown where it failed, not an offline state.

let online = typeof navigator === 'undefined' ? true : navigator.onLine !== false
const listeners = new Set<() => void>()
const reconnects = new Set<() => void>()

function set(next: boolean) {
  if (next === online) return
  online = next
  listeners.forEach((l) => l())
  if (next) reconnects.forEach((r) => r())
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => set(true))
  window.addEventListener('offline', () => set(false))
}

export function isOnline() {
  return online
}

// For tests: what the online/offline events would do.
export function setOnlineForTest(next: boolean) {
  set(next)
}

export function useOnline() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => online,
    () => true
  )
}

// What a refused write says (h 4067-4073): what did not happen, and that
// nothing typed is lost.
export const OFFLINE_WRITE = 'That needs a connection, so nothing was saved. Nothing you have entered is lost: try again once you are back online.'

// A historical or filtered view refetches without being asked (h 4167-4171):
// on window focus and on reconnect after the offline banner clears. A filter
// or date change refetches through the view's own load. `load` is called with
// no arguments; the latest one is always used.
export function useRefetch(load: () => void, enabled = true) {
  const ref = useRef(load)
  ref.current = load
  useEffect(() => {
    if (!enabled) return
    const run = () => ref.current()
    const onFocus = () => {
      if (online) run()
    }
    window.addEventListener('focus', onFocus)
    reconnects.add(run)
    return () => {
      window.removeEventListener('focus', onFocus)
      reconnects.delete(run)
    }
  }, [enabled])
}
